/**
 * LoadsTable Component
 * HTML table for displaying loads with expandable rows.
 *
 * Supports two modes:
 *  - Uncontrolled (default): manages sort + expand state internally. load-hub uses this.
 *  - Controlled: caller passes sort/expandedIds/viewedIds + change callbacks. agentic-hub uses this.
 *
 * Optional extension points (Commit C):
 *  - getRowMeta: returns per-row decorations (isPinned, bestFit, brokerReplied, status)
 *  - rowActions: render prop for the rightmost action cell
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react'
import { CanonicalLoad } from '@numeo/types-core'
import { cn } from 'utils/cn'
import {
    TelegramIcon,
    ParadeIcon,
    HappyRobotIcon,
    TqlIcon,
    ItfIcon,
    PlsIcon,
    BeonIcon,
    NavisphereIcon,
    ArriveIcon,
    SpotIcon,
    EmergeIcon,
    ModeGlobalIcon,
    KnxIcon,
    EchoIcon,
    SchneiderIcon,
    DatOneIcon,
} from 'features/load-hub/icons'
import { HAS_EXTENSION_KEY } from 'features/load-hub/utils/constants'
import { getLoadUid } from 'features/load-hub/utils/loadKey'
import { LocationData } from 'features/load-hub/types/filters'
import { LoadRow } from './LoadRow'
import { VisibilityOff } from '@mui/icons-material'
import { EmptyState, ProgressBar } from 'components/ds'
import { LoadFiltersState } from 'features/load-hub/types/filters'
import { EmptyStateIcon } from 'features/load-hub/icons'
import type { SortDir, SortColumn, LoadsTableSort, LoadColumnId } from './types'
import { DEFAULT_SORT, DEFAULT_LOAD_COLUMN_ORDER } from './types'
import { compareLoadsBy } from './loadMetrics'

/** 3-state filter indicator — click cycles: none → desc → asc → none.
 * Icon rotates 180° to visually hint direction (desc = default bars, asc = flipped).
 * Exported so other loadboards (e.g. the box-truck board) reuse the exact same sort-header control. */
export const FilterIndicator: React.FC<{ sort: SortDir; onSort: (next: SortDir) => void }> = ({ sort, onSort }) => {
    const next: SortDir = sort === 'none' ? 'desc' : sort === 'desc' ? 'asc' : 'none'
    const title = sort === 'desc' ? 'Click to sort ascending' : sort === 'asc' ? 'Click to clear sort' : 'Click to sort descending'
    const active = sort !== 'none'
    return (
        <button
            onClick={() => onSort(next)}
            className={cn(
                'inline-flex items-center justify-center gap-0.5 transition-colors cursor-pointer',
                // Active sort renders as a bordered pill with a subtle bg + accent color so the
                // column header reads "this is the active sort, direction = X" at a glance.
                // Inactive stays as a minimal icon-only button so headers don't look noisy.
                active
                    ? 'ml-1 h-5 px-1 rounded-md border border-[var(--lh-border)] bg-[var(--lh-surface)] text-[var(--lh-accent)] hover:bg-[var(--lh-surface-secondary)]'
                    : 'ml-0.5 border-none bg-transparent p-0 text-[var(--lh-fg-muted)] hover:text-[var(--lh-fg)]'
            )}
            title={title}
        >
            {/* Direction arrow — only shown when sort is active. ↑ for asc, ↓ for desc. */}
            {active && (
                <svg
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                >
                    {sort === 'asc' ? <path d="M12 19V5M5 12l7-7 7 7" /> : <path d="M12 5v14M5 12l7 7 7-7" />}
                </svg>
            )}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M3 12h12M3 18h6" />
            </svg>
        </button>
    )
}

/** Per-row meta returned by the optional getRowMeta callback.
 * All fields are optional — unset fields produce no decoration. */
export interface RowMeta {
    isPinned?: boolean
    bestFit?: boolean
    brokerReplied?: boolean
    status?: string
    /** When true, this load has at least one pending agent question / approval.
     *  Renders a pulsing amber hand-raised badge in the Contact column. */
    hasPendingQuestion?: boolean
    /** Optional stable row number from the backend (agentic-hub's CanonicalSearchLoad.row_number).
     *  When set, the "#" column shows this value instead of the visual index, so the
     *  chat agent's references to row N stay valid across sort/filter changes. */
    rowNumber?: number
    /** Optional AI-generated explanation for the status. Surfaced as a tooltip on the
     *  Status chip so the dispatcher can see *why* the agent assigned that status. */
    statusReason?: string
    /** Live per-load streaming state from the agentic-hub email/chat agent. When set,
     *  the row renders a small spinner + phase label to signal "agent is working on this
     *  load right now". Absent in load-hub. */
    streamingState?: {
        phase: 'thinking' | 'tool-call' | 'approval-pending' | 'done'
        error?: string
        /** True when the run was cancelled by a new user-initiated search (AH_Superseded).
         *  Drives a gray "AI stopped" pill — distinct from the red "AI error" pill. */
        superseded?: boolean
        /** Set on `tool-call` phase. The status pill renders a tool-specific label
         *  ('Drafting Email...' / 'Checking Rate...' / 'Searching...' / fallback). */
        toolName?: string
        /** Set on `done` phase. Drives the checkmark + status pill replacement
         *  ('Processing complete' style) for a beat before status takes over. */
        finalContent?: string
    }
    /** Optional restored timestamp — when set, a cyan "restored" badge renders
     *  inline next to the status pill so the dispatcher knows a load was un-archived. */
    restoredAt?: string | null
    /** Optional load-id-keyed score (0-1). Surfaced as part of the status pill tooltip
     *  ("status_reason | score N%"). */
    score?: number | null
    /** Signature of the per-load reload market (`destinationMarket`) the injected Destination cell reads.
     *  Lets LoadRow's memo re-render the cell when market intel lands late (e.g. via websocket
     *  after the row already mounted) without comparing the map-closing `renderDestinationCell`
     *  identity, which changes on every load update and would defeat the memo during streaming. */
    marketIntelKey?: string
    /** Resolved palette + label for the `status`. Agentic-hub passes the simplified
     *  bucket style; load-hub omits and the row falls back to <StatusChip>. Owning
     *  the palette in the feature avoids a cross-package dependency from
     *  `components/loads/` to `features/`. When `isLoading` is set, the row renders
     *  a spinner instead of a chip — used while the load is being filtered/ranked. */
    statusStyle?: { bg: string; text: string; dot: string; label: string; isLoading?: boolean }
    /** Prefetched factoring grade from the search pipeline (agentic-hub). When set,
     *  the Company cell renders an inline factoring badge immediately without
     *  waiting for the user to expand the row. Load-hub omits this and falls back
     *  to the post-expand <FactoringBadge mcNumber> API-driven badge. */
    factoring?: { grade: string; source?: string; loadsCount?: number; color?: string }
    /** AI fit-classification KEY ('recommended' | 'matched' | 'potential' |
     *  'duplicate' | 'not_matched'), derived from base_tier (fallback status) —
     *  orthogonal to the workflow status. null for unscored / in-flight loads.
     *  Rendered far-left as a colored icon (+ row left-border) via
     *  `loadClassification.tsx`, which maps the key → icon / color / label. */
    classification?: string | null
}

interface LoadsTableProps {
    loads: CanonicalLoad[]
    selectedLoads: number[]
    hiddenLoads: Set<string>
    onSelectLoad: (id: number) => void
    onSelectAll: (checked: boolean) => void
    onLoadUpdate?: (updatedLoad: CanonicalLoad) => void
    onEmailClick?: (load: CanonicalLoad) => void
    /** Enables Load Hub-only interaction telemetry in shared row components. */
    analyticsSurface?: 'load_hub'
    /** Called when the contacted-broker email pill is clicked (existing thread).
     * Receives the load + threadId so the consumer (agentic-hub) can open the
     * email rail focused on that conversation. Falls back to expand-row if absent. */
    onOpenThread?: (load: CanonicalLoad, threadId: string) => void
    sendingLoads?: Set<string>
    errorLoads?: Set<string>
    emailThreads?: Map<string, string>
    /** Voice-call trigger (agentic-hub only). Receives the row's CanonicalLoad on click. */
    onCallClick?: (load: CanonicalLoad) => void
    hasPhoneLoads?: Set<string>
    callingLoads?: Set<string>
    errorCallLoads?: Set<string>
    /** Set of loadUids whose AI-Hub calls are currently live. Multiple concurrent calls
     *  are supported per dispatcher (separate workers per broker), so this is a Set —
     *  each row checks if its own uid is present. */
    activeCallLoadUids?: Set<string>
    /** Set of loadUids that have any prior call (call_history or last_call_id). When a
     *  row's uid is present, the PhoneIcon renders green — same pattern as email's
     *  emailThreadId → green ManualChatIcon. */
    calledLoadUids?: Set<string>
    /** Render prop the consumer (agentic-hub) supplies to inject inline call-control
     *  buttons (Listen / Join / End) when a row's call is initiating or live. Returning
     *  null lets LoadRow fall back to the regular email + phone icons. */
    renderCallControls?: (loadUid: string) => React.ReactNode | null
    loadMongoIds?: Map<string, string>
    unreadCounts?: Map<string, number>
    onResetUnread?: (loadUid: string) => void
    onLoadMore?: () => void
    hasMore?: boolean
    isLoadingMore?: boolean
    originLocation?: string
    originLocationData?: LocationData | null
    onRefresh?: () => void
    isRefreshing?: boolean
    /** When true, the table scrolls back to the top whenever a refresh brings in a
     *  load that wasn't present before (used by load-hub's Auto Refresh "Scroll to new"). */
    autoScrollToNewLoads?: boolean
    /** Uids of loads to flag as "just arrived" — a green left border, DAT-style. The consumer owns
     *  the detection (see useNewLoadHighlight) and MUST derive it from the pre-client-filter load set
     *  so search/hide-duplicates/source toggles never re-flag already-seen loads. Absent → no cue. */
    newLoadUids?: ReadonlySet<string>
    filters?: LoadFiltersState
    onFilterChange?: (key: keyof LoadFiltersState, value: string | string[]) => void
    onApplyClientFilters?: () => void
    onResetClientFilters?: () => void
    isClientFiltersApplied?: boolean
    onHideSelected?: () => void
    loading?: boolean
    hideDuplicates?: boolean
    onToggleHideDuplicates?: () => void
    /** External search query from LoadFilters (lifted to parent) */
    searchQuery?: string

    // ── Controlled sort (optional) ────────────────────────────────────────────────
    /** When provided, sort state is controlled externally. If omitted, internal state is used. */
    sort?: LoadsTableSort
    /** Called when user clicks a sort column header. Only fires in controlled mode. */
    onSortChange?: (column: SortColumn, direction: SortDir) => void

    // ── Controlled expand (optional) ──────────────────────────────────────────────
    /** When provided, expand state is controlled externally. If omitted, internal state is used. */
    expandedIds?: Set<string>
    /** Called when a row is toggled. Only fires in controlled mode. */
    onExpandToggle?: (loadUid: string) => void
    /** When provided, viewed state is controlled externally. */
    viewedIds?: Set<string>

    // ── Extension points for agentic-hub ─────────────────────────────────────────
    /** Optional per-row metadata callback. Returns decorations (pin, bestFit, etc.). */
    getRowMeta?: (load: CanonicalLoad) => RowMeta | undefined
    /** When true, rows with a rowNumber are draggable (agentic-hub → Ask Numeo chat). */
    draggableRows?: boolean
    /** Optional render prop for per-row action menu (rendered after Contact column). */
    rowActions?: (load: CanonicalLoad) => React.ReactNode
    /** Optional render prop for inline controls inside the Company cell (right of the broker
     *  envelope icon. Used by agentic-hub for the block/favorite broker (star) toggle.
     *  Absent in load-hub. */
    renderContactExtras?: (load: CanonicalLoad) => React.ReactNode
    /** Optional pin toggle (agentic-hub). When provided, an inline pin button is
     *  rendered next to the row number; clicking flips the pinned state. */
    onPinRow?: (load: CanonicalLoad, pinned: boolean) => void
    /** Optional render prop that replaces the Rate column body. Agentic-hub passes
     *  RateHistoryCell so the rate doubles as a tooltip carrying broker rate history.
     *  Absent in load-hub. */
    renderRateCell?: (load: CanonicalLoad) => React.ReactNode
    /** Optional render prop that replaces the RPM column body. Agentic-hub passes a
     *  MarketRpmCell variant carrying the dotted-underline market-rate affordance +
     *  Numeo / DAT-IQ range tooltip. Absent in load-hub. */
    renderRpmCell?: (load: CanonicalLoad) => React.ReactNode
    /** Optional render prop that replaces the Destination column body. Agentic-hub passes
     *  a variant carrying a Market Intel (destination reload-market) hover tooltip. When
     *  absent (load-hub), the default destination cell renders unchanged. */
    renderDestinationCell?: (load: CanonicalLoad) => React.ReactNode
    /** Optional render prop that replaces the expanded-row body entirely. Agentic-hub
     *  uses this to render LoadDetailPanel (full info column + trip schedule + map +
     *  market rate cards) instead of load-hub's simpler LoadExpandedRow. */
    renderExpandedContent?: (load: CanonicalLoad) => React.ReactNode

    // ── Agentic-hub extra columns (defaults false → no change for load-hub) ──────
    /** Render a leading "#" column showing the 1-based row number. */
    showRowNumber?: boolean
    /** Render a "Status" column (colored pill) after Age. Value comes from getRowMeta().status. */
    showStatusColumn?: boolean
    /** Render a leading AI-classification icon column (far-left) + a matching row
     *  left-border. Value comes from getRowMeta().classification. Agentic-hub only. */
    showClassColumn?: boolean
    /** Optional render-prop replacing the plain "Status" header label. Agentic-hub injects
     *  the AgentStatusFilter popover here. When undefined, the plain "Status" string renders. */
    statusHeaderSlot?: React.ReactNode
    /** Render a star (pin) toggle column on the right side. */
    showStarColumn?: boolean
    /** Render a close (archive) column on the right side. */
    showCloseColumn?: boolean
    /** Called when the close column button is clicked for a row. */
    onCloseRow?: (load: CanonicalLoad) => void
    /** Set of load UIDs currently being processed (sync delta — filtering/ranking).
     *  When non-empty, those rows are partitioned out of the main list and shown
     *  behind a collapsible "N new loads being processed" banner pinned at the top
     *  of the tbody. Matches the legacy agentic-hub Sync UX. */
    processingLoadUids?: Set<string>
    /** Load UID (externalId) whose email conversation is currently open in the right
     *  email rail. The matching row is tinted blue + left-bordered so the dispatcher
     *  can see which load the open conversation belongs to. Agentic-hub only; load-hub
     *  omits it (no highlight). */
    emailOpenLoadUid?: string | null
    /** When true, LoadsTable does NOT render its built-in "No loads found" EmptyState.
     *  The caller is expected to render its own contextual overlay (e.g. agentic-hub's
     *  filter-aware emptyState in ResultsView). Without this, both empties stack. */
    suppressEmptyState?: boolean
    /** Opt-in row windowing for large result sets (load-hub). Above ~150 rows only the
     *  rows near the viewport mount as real <LoadRow>s; off-screen runs collapse into
     *  fixed-height spacer <tr>s. Off by default because consumers that reach rows via
     *  document.querySelector (agentic-hub's focusLoadRow "#123" chat references) need
     *  every row in the DOM. */
    virtualizeRows?: boolean
    /** Extra scrollable space appended below the last row. agentic-hub's ResultsView passes this so the
     *  floating "Ask Numeo AI" pill doesn't cover the last loads when scrolled to the bottom. Accepts a
     *  px number or any CSS length. Omit elsewhere. */
    bottomSpacerHeight?: number | string
    // ── Reorderable columns (agentic-hub opt-in; load-hub omits → fixed order) ──
    /** When true, the movable data-column headers are draggable to reorder. Pair with
     *  `columnOrder` + `onReorderColumns` (controlled). */
    reorderableColumns?: boolean
    /** Controlled order of the movable data columns — applied to the header, skeleton, and
     *  every row. Undefined → DEFAULT_LOAD_COLUMN_ORDER (unchanged layout). */
    columnOrder?: LoadColumnId[]
    /** Fired when a header is dropped onto another — move `fromId` before `toId`, or after it when
     *  `placeAfter` (drop past the midpoint / onto the last column). */
    onReorderColumns?: (fromId: LoadColumnId, toId: LoadColumnId, placeAfter: boolean) => void
}

// ── Skeleton rows ────────────────────────────────────────────────────────────
// Shown inside <tbody> while a search is in flight and no rows exist yet.
// Mirrors LoadRow's column structure so the header doesn't shift when real rows
// arrive. Each cell is a pulsing grey bar shaped roughly like the content it
// will be replaced by.

const SkeletonBar: React.FC<{ widthClass: string }> = ({ widthClass }) => (
    <span className={cn('inline-block h-3 rounded bg-[var(--lh-border)]/70 dark:bg-white/[0.06] animate-pulse align-middle', widthClass)} />
)

const SkeletonRows: React.FC<{
    count: number
    showClassColumn: boolean
    showRowNumber: boolean
    hasPinColumn: boolean
    showStatusColumn: boolean
    hasActionsCol: boolean
    showStarColumn: boolean
    showCloseColumn: boolean
    columnOrder: LoadColumnId[]
}> = ({ count, showClassColumn, showRowNumber, hasPinColumn, showStatusColumn, hasActionsCol, showStarColumn, showCloseColumn, columnOrder }) => (
    <>
        {Array.from({ length: count }).map((_, i) => {
            const isOdd = i % 2 === 1
            const bgClass = isOdd ? 'bg-[var(--lh-row-zebra-b)]' : 'bg-[var(--lh-row-zebra-a)]'
            return (
                <tr key={`skeleton-${i}`} className={cn('h-8', bgClass)}>
                    {showClassColumn && (
                        <td className="px-1 text-center">
                            <SkeletonBar widthClass="w-3.5" />
                        </td>
                    )}
                    {showRowNumber && (
                        <td className="px-2 text-center">
                            <SkeletonBar widthClass="w-4" />
                        </td>
                    )}
                    {hasPinColumn && (
                        <td className="px-1 text-center">
                            <SkeletonBar widthClass="w-3" />
                        </td>
                    )}
                    {(() => {
                        // Movable skeleton bars keyed by column id, emitted in `columnOrder` so the
                        // loading placeholder lines up with the (possibly reordered) header + rows.
                        const cells: Partial<Record<LoadColumnId, React.ReactNode>> = {
                            age: (
                                <td key="age" className="px-2">
                                    <SkeletonBar widthClass="w-8" />
                                </td>
                            ),
                            src: (
                                <td key="src" className="px-2">
                                    <SkeletonBar widthClass="w-5" />
                                </td>
                            ),
                            rate: (
                                <td key="rate" className="px-2">
                                    <SkeletonBar widthClass="w-12" />
                                </td>
                            ),
                            rpm: (
                                <td key="rpm" className="px-2">
                                    <SkeletonBar widthClass="w-10" />
                                </td>
                            ),
                            miles: (
                                <td key="miles" className="px-2">
                                    <SkeletonBar widthClass="w-10" />
                                </td>
                            ),
                            map: (
                                <td key="map" className="px-2">
                                    <SkeletonBar widthClass="w-3" />
                                </td>
                            ),
                            origin: (
                                <td key="origin" className="px-2">
                                    <SkeletonBar widthClass="w-20" />
                                </td>
                            ),
                            dhO: (
                                <td key="dhO" className="px-2">
                                    <SkeletonBar widthClass="w-8" />
                                </td>
                            ),
                            destination: (
                                <td key="destination" className="px-2">
                                    <SkeletonBar widthClass="w-20" />
                                </td>
                            ),
                            dhD: (
                                <td key="dhD" className="px-2">
                                    <SkeletonBar widthClass="w-8" />
                                </td>
                            ),
                            equipment: (
                                <td key="equipment" className="px-2">
                                    <SkeletonBar widthClass="w-28" />
                                </td>
                            ),
                            company: (
                                <td key="company" className="px-2">
                                    <SkeletonBar widthClass="w-24" />
                                </td>
                            ),
                            contact: (
                                <td key="contact" className="px-2">
                                    <SkeletonBar widthClass="w-28" />
                                </td>
                            ),
                        }
                        if (showStatusColumn) {
                            cells.status = (
                                <td key="status" className="px-2 w-[150px] min-w-[150px] max-w-[150px]">
                                    <SkeletonBar widthClass="w-16" />
                                </td>
                            )
                        }
                        return columnOrder.map((id) => cells[id])
                    })()}
                    {hasActionsCol && (
                        <td className="px-2">
                            <SkeletonBar widthClass="w-6" />
                        </td>
                    )}
                    {showStarColumn && (
                        <td className="px-2">
                            <SkeletonBar widthClass="w-3" />
                        </td>
                    )}
                    {showCloseColumn && (
                        <td className="px-2">
                            <SkeletonBar widthClass="w-3" />
                        </td>
                    )}
                </tr>
            )
        })}
    </>
)

export const LoadsTable: React.FC<LoadsTableProps> = ({
    loads,
    selectedLoads,
    hiddenLoads,
    onSelectLoad,
    onLoadUpdate,
    onEmailClick,
    analyticsSurface,
    onOpenThread,
    sendingLoads,
    errorLoads,
    emailThreads,
    onCallClick,
    hasPhoneLoads,
    callingLoads,
    errorCallLoads,
    activeCallLoadUids,
    calledLoadUids,
    renderCallControls,
    loadMongoIds,
    unreadCounts,
    onResetUnread,
    onLoadMore,
    hasMore = false,
    isLoadingMore = false,
    originLocation = '',
    originLocationData = null,
    isRefreshing = false,
    onHideSelected,
    loading = false,
    hideDuplicates = false,
    searchQuery = '',
    // Controlled props
    sort: sortProp,
    onSortChange,
    expandedIds: expandedIdsProp,
    onExpandToggle,
    viewedIds: viewedIdsProp,
    // Extension points
    getRowMeta,
    draggableRows = false,
    rowActions,
    renderContactExtras,
    renderRateCell,
    renderRpmCell,
    renderDestinationCell,
    renderExpandedContent,
    onPinRow,
    showRowNumber = false,
    showStatusColumn = false,
    showClassColumn = false,
    statusHeaderSlot,
    showStarColumn = false,
    showCloseColumn = false,
    onCloseRow,
    processingLoadUids,
    emailOpenLoadUid,
    suppressEmptyState = false,
    virtualizeRows = false,
    bottomSpacerHeight,
    reorderableColumns = false,
    columnOrder,
    onReorderColumns,
    autoScrollToNewLoads = false,
    newLoadUids,
}) => {
    const isControlledSort = sortProp !== undefined
    const isControlledExpand = expandedIdsProp !== undefined

    const tableContainerRef = useRef<HTMLDivElement>(null)

    // ── Scroll-position anchor ────────────────────────────────────────────────
    // Keep the user's visual position stable when the loads array changes
    // (streamed prepends, reorder by pin/status, sort flips). Snapshot the first
    // visible row using scrollTop + offsetTop (no forced reflow), then restore
    // its position after React commits. Uses offsetTop instead of
    // getBoundingClientRect to avoid forcing synchronous layout during render.
    const scrollAnchorRef = useRef<{ rowAttr: string; visualOffset: number } | null>(null)
    const prevLoadsRef = useRef(loads)
    if (loads !== prevLoadsRef.current) {
        prevLoadsRef.current = loads
        const el = tableContainerRef.current
        if (el) {
            const rows = el.querySelectorAll<HTMLTableRowElement>('tr[data-row]')
            const scrollTop = el.scrollTop
            scrollAnchorRef.current = null
            for (const row of rows) {
                const rowTop = row.offsetTop - scrollTop
                if (rowTop + row.offsetHeight > 0) {
                    scrollAnchorRef.current = { rowAttr: row.getAttribute('data-row')!, visualOffset: rowTop }
                    break
                }
            }
        } else {
            scrollAnchorRef.current = null
        }
    }
    useLayoutEffect(() => {
        const container = tableContainerRef.current
        if (!container) return
        const anchor = scrollAnchorRef.current
        if (!anchor) return
        const row = container.querySelector<HTMLTableRowElement>(`tr[data-row="${anchor.rowAttr}"]`)
        if (!row) return
        const newVisualOffset = row.offsetTop - container.scrollTop
        const delta = newVisualOffset - anchor.visualOffset
        if (Math.abs(delta) > 1) container.scrollTop += delta
        scrollAnchorRef.current = null
    }, [loads])

    // How many skeleton rows to render while loading — derived from the scroll
    // container's height so the skeleton fills the viewport (no empty space below).
    // Row height matches the LoadRow's ~32px (h-8 in skeleton + zebra borders).
    const SKELETON_ROW_PX = 32
    const SKELETON_HEADER_PX = 40
    const [skeletonRowCount, setSkeletonRowCount] = useState(12)
    useEffect(() => {
        const el = tableContainerRef.current
        if (!el) return
        const recompute = () => {
            const h = el.clientHeight
            if (h <= 0) return
            const rows = Math.max(8, Math.ceil((h - SKELETON_HEADER_PX) / SKELETON_ROW_PX))
            setSkeletonRowCount(rows)
        }
        recompute()
        const observer = new ResizeObserver(recompute)
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    // ── Row windowing (virtualization, opt-in via virtualizeRows) ─────────────
    // Above VIRTUALIZE_THRESHOLD rows, only the rows near the viewport render as
    // real <LoadRow>s; runs of off-screen rows collapse into single fixed-height
    // spacer <tr>s so the scrollbar geometry stays exact. Expanded rows always
    // render for real (their height is content-driven and must stay in layout).
    // Below the threshold — or when virtualizeRows is off — the table renders
    // exactly as before: same DOM, same behavior.
    const VIRTUALIZE_THRESHOLD = 150
    const OVERSCAN_ROWS = 15
    const [rowH, setRowH] = useState(33)
    const [scrollWindow, setScrollWindow] = useState({ top: 0, height: 800 })

    const updateScrollWindow = useCallback(
        (el: HTMLElement) => {
            if (!virtualizeRows) return
            setScrollWindow((prev) => {
                const top = el.scrollTop
                const height = el.clientHeight || prev.height
                // Quantize updates to ~5-row steps — OVERSCAN_ROWS absorbs the drift, and the
                // whole tbody isn't re-rendered on every scrolled pixel.
                if (Math.abs(top - prev.top) < rowH * 5 && Math.abs(height - prev.height) < rowH) return prev
                return { top, height }
            })
        },
        [virtualizeRows, rowH]
    )

    useEffect(() => {
        if (!virtualizeRows) return
        const el = tableContainerRef.current
        if (!el) return
        updateScrollWindow(el)
        const observer = new ResizeObserver(() => updateScrollWindow(el))
        observer.observe(el)
        return () => observer.disconnect()
    }, [virtualizeRows, updateScrollWindow])

    // Internal state — only used when NOT controlled
    const [internalExpandedIds, setInternalExpandedIds] = useState<Set<string>>(new Set())
    const [internalViewedIds, setInternalViewedIds] = useState<Set<string>>(new Set())
    const [internalSort, setInternalSort] = useState<LoadsTableSort>(DEFAULT_SORT)

    // Processing-row expansion (sync delta banner above the regular rows)
    const [processingExpanded, setProcessingExpanded] = useState(false)
    const processingCount = processingLoadUids?.size ?? 0
    useEffect(() => {
        if (processingCount === 0) setProcessingExpanded(false)
    }, [processingCount])

    // Resolved state — controlled wins over internal. Inactive rows are also used
    // outside live search, so their details remain available to every consumer.
    const sort = isControlledSort ? sortProp : internalSort
    const resolvedExpandedLoadIds = isControlledExpand ? expandedIdsProp! : internalExpandedIds
    const expandedLoadIds = resolvedExpandedLoadIds
    const viewedLoads = viewedIdsProp ?? internalViewedIds

    const { rate: rateSort, rpm: rpmSort, age: ageSort, dhO: dhOSort, dhD: dhDSort } = sort

    useEffect(() => {
        if (typeof window !== 'undefined' && !localStorage.getItem(HAS_EXTENSION_KEY)) {
            localStorage.setItem(HAS_EXTENSION_KEY, 'false')
        }
    }, [])

    useEffect(() => {
        const styleId = 'loads-table-scrollbar-styles'
        if (document.getElementById(styleId)) return

        const style = document.createElement('style')
        style.id = styleId
        style.textContent = `
            .loads-table-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
            .loads-table-scroll::-webkit-scrollbar-track { background: transparent; }
            .loads-table-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }
            .loads-table-scroll::-webkit-scrollbar-thumb:hover { background: #9CA3AF; }
            .dark .loads-table-scroll::-webkit-scrollbar-thumb { background: #4B5563; }
            @keyframes slideRight {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(350%); }
            }
        `
        document.head.appendChild(style)
        return () => {
            document.getElementById(styleId)?.remove()
        }
    }, [])

    // O(1) selection lookups — selectedLoads.includes() per row is O(n²) at 1000+ rows
    const selectedSet = useMemo(() => new Set(selectedLoads), [selectedLoads])

    const { filteredLoads, duplicateCount } = useMemo(() => {
        const visible = loads.filter((load) => !hiddenLoads.has(load.id?.toString()))
        // Deduplicate by externalId to prevent duplicate React keys
        const seenKeys = new Set<string>()
        const unique = visible.filter((load) => {
            const key = load.externalId || `${load.sourceType}|${load.id}`
            if (seenKeys.has(key)) return false
            seenKeys.add(key)
            return true
        })
        // Count content duplicates (same route + rate + miles + equipment)
        const seenContent = new Set<string>()
        let dupCount = 0
        const deduped = unique.filter((load) => {
            const contentKey = [
                (load.pickupCity ?? '').toLowerCase(),
                (load.pickupStateProv ?? '').toLowerCase(),
                (load.deliveryCity ?? '').toLowerCase(),
                (load.deliveryStateProv ?? '').toLowerCase(),
                load.rateAmountUsd ?? '',
                load.distanceMiles ?? '',
                load.equipmentType ?? '',
            ].join('|')
            if (seenContent.has(contentKey)) {
                dupCount++
                return false
            }
            seenContent.add(contentKey)
            return true
        })
        return { filteredLoads: hideDuplicates ? deduped : unique, duplicateCount: dupCount }
    }, [loads, hiddenLoads, hideDuplicates])

    const searchedLoads = useMemo(() => {
        if (!searchQuery.trim()) return filteredLoads
        try {
            const regex = new RegExp(searchQuery.trim(), 'i')
            return filteredLoads.filter((load) => {
                const fields = [
                    load.id?.toString(),
                    load.loadID?.toString(),
                    load.externalId,
                    load.pickupCity,
                    load.pickupStateProv,
                    load.deliveryCity,
                    load.deliveryStateProv,
                    load.brokerCompanyName,
                    load.brokerContactEmail,
                    load.brokerContactPhone,
                    load.brokerContactName,
                ]
                return fields.some((f) => f && regex.test(f))
            })
        } catch {
            // Invalid regex — fall back to plain includes
            const q = searchQuery.trim().toLowerCase()
            return filteredLoads.filter((load) => {
                const fields = [
                    load.id?.toString(),
                    load.loadID?.toString(),
                    load.externalId,
                    load.pickupCity,
                    load.pickupStateProv,
                    load.deliveryCity,
                    load.deliveryStateProv,
                    load.brokerCompanyName,
                    load.brokerContactEmail,
                    load.brokerContactPhone,
                    load.brokerContactName,
                ]
                return fields.some((f) => f && f.toLowerCase().includes(q))
            })
        }
    }, [filteredLoads, searchQuery])

    const visibleLoads = useMemo(() => {
        // Exactly one column is ever active (handleColumnSort clears the others). Resolve it in the original
        // branch priority (age → dhO → dhD → rate → rpm) and delegate to the shared comparator so this table and
        // the box-truck board sort identically. Pinned rows still float above their group.
        const active: SortColumn | null =
            ageSort !== 'none'
                ? 'age'
                : dhOSort !== 'none'
                  ? 'dhO'
                  : dhDSort !== 'none'
                    ? 'dhD'
                    : rateSort !== 'none'
                      ? 'rate'
                      : rpmSort !== 'none'
                        ? 'rpm'
                        : null
        if (!active) return searchedLoads
        const dir = ({ age: ageSort, dhO: dhOSort, dhD: dhDSort, rate: rateSort, rpm: rpmSort }[active] === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc'
        return [...searchedLoads].sort((a, b) => {
            // Pinned rows always sort above unpinned, regardless of which column the user picked.
            const pinDiff = (getRowMeta?.(a)?.isPinned ? 0 : 1) - (getRowMeta?.(b)?.isPinned ? 0 : 1)
            if (pinDiff !== 0) return pinDiff
            return compareLoadsBy(a, b, active, dir)
        })
    }, [searchedLoads, rateSort, rpmSort, ageSort, dhOSort, dhDSort, getRowMeta])

    // Partition out loads currently being processed (sync delta — filtering/ranking).
    // Those are hidden from the main list and pooled into the collapsible banner row
    // at the top of the tbody. Once their status terminates upstream, processingLoadUids
    // drops them and they reappear in the main list.
    const { mainLoads, processingLoads } = useMemo(() => {
        if (!processingLoadUids || processingLoadUids.size === 0) {
            return { mainLoads: visibleLoads, processingLoads: [] as CanonicalLoad[] }
        }
        const main: CanonicalLoad[] = []
        const processing: CanonicalLoad[] = []
        for (const l of visibleLoads) {
            if (processingLoadUids.has(getLoadUid(l))) processing.push(l)
            else main.push(l)
        }
        return { mainLoads: main, processingLoads: processing }
    }, [visibleLoads, processingLoadUids])

    // Auto-scroll to the top when new loads arrive (opt-in via Auto Refresh's
    // "Scroll to new"). Compare load-uid sets so a re-sort — same loads, new order —
    // never triggers it; only a genuinely new load does. Skips the first run so an
    // initial load never yanks the view.
    const prevLoadUidsRef = useRef<Set<string> | null>(null)
    useEffect(() => {
        const uids = new Set(mainLoads.map(getLoadUid))
        const prev = prevLoadUidsRef.current
        prevLoadUidsRef.current = uids
        if (!autoScrollToNewLoads || prev === null) return
        const hasNew = [...uids].some((uid) => !prev.has(uid))
        if (hasNew) tableContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }, [mainLoads, autoScrollToNewLoads])

    const virtualize = virtualizeRows && mainLoads.length > VIRTUALIZE_THRESHOLD

    // Window bounds in row indices. scrollTop/rowH over-estimates the start index when
    // expanded rows (taller than rowH) sit above the viewport, so widen the top margin
    // per expanded row — rendering a few extra rows is cheap, a blank visible row is not.
    const expandedMarginRows = virtualize ? Math.min(expandedLoadIds.size, 8) * 25 : 0
    const winStart = virtualize ? Math.max(0, Math.floor(scrollWindow.top / rowH) - OVERSCAN_ROWS - expandedMarginRows) : 0
    const winEnd = virtualize ? Math.min(mainLoads.length, Math.ceil((scrollWindow.top + scrollWindow.height) / rowH) + OVERSCAN_ROWS) : mainLoads.length

    // Measure the real collapsed-row height from the DOM so spacer math matches the
    // actual layout (density/theme can change it). Main rows carry data-row; the
    // expanded detail <tr> does not, so the first match is always a collapsed-height row.
    useLayoutEffect(() => {
        if (!virtualize) return
        const el = tableContainerRef.current
        if (!el) return
        const row = el.querySelector<HTMLTableRowElement>('tbody tr[data-row]')
        if (row) {
            const h = row.offsetHeight
            if (h > 0 && Math.abs(h - rowH) > 1) setRowH(h)
        }
    }, [virtualize, mainLoads.length, rowH])

    // Sort handlers — resets all other columns so only one is active at a time.
    // In controlled mode: delegates to onSortChange. In uncontrolled mode: updates internal state.
    const handleColumnSort = useCallback(
        (column: SortColumn, next: SortDir) => {
            if (isControlledSort) {
                onSortChange?.(column, next)
            } else {
                const reset: LoadsTableSort = { rate: 'none', rpm: 'none', age: 'none', dhO: 'none', dhD: 'none' }
                setInternalSort({ ...reset, [column]: next })
                onSortChange?.(column, next)
            }
        },
        [isControlledSort, onSortChange]
    )

    const handleRateSort = useCallback((next: SortDir) => handleColumnSort('rate', next), [handleColumnSort])
    const handleRpmSort = useCallback((next: SortDir) => handleColumnSort('rpm', next), [handleColumnSort])
    const handleAgeSort = useCallback((next: SortDir) => handleColumnSort('age', next), [handleColumnSort])
    const handleDhOSort = useCallback((next: SortDir) => handleColumnSort('dhO', next), [handleColumnSort])
    const handleDhDSort = useCallback((next: SortDir) => handleColumnSort('dhD', next), [handleColumnSort])

    // ── Column drag-to-reorder (agentic-hub opt-in) ─────────────────────────────
    // Native HTML5 DnD on the movable header cells. dragColId = the column being dragged,
    // overColId = the header currently under the cursor (drop-before target). All no-ops unless
    // `reorderableColumns`, so load-hub headers behave exactly as before.
    const [dragColId, setDragColId] = useState<LoadColumnId | null>(null)
    const [overColId, setOverColId] = useState<LoadColumnId | null>(null)
    // Which edge of the hovered header the drop lands on — right half → after (lets a column reach
    // the last slot), left half → before.
    const [overAfter, setOverAfter] = useState(false)
    const clearColDrag = useCallback(() => {
        setDragColId(null)
        setOverColId(null)
    }, [])
    // True when the cursor is past the horizontal midpoint of the target header.
    const isAfterMidpoint = (e: React.DragEvent<HTMLTableCellElement>): boolean => {
        const rect = e.currentTarget.getBoundingClientRect()
        return e.clientX > rect.left + rect.width / 2
    }
    const headerDragProps = (id: LoadColumnId): React.HTMLAttributes<HTMLTableCellElement> => {
        if (!reorderableColumns) return {}
        return {
            draggable: true,
            onDragStart: (e) => {
                setDragColId(id)
                e.dataTransfer.effectAllowed = 'move'
                e.dataTransfer.setData('text/plain', id) // some browsers need data set to begin a drag
            },
            onDragOver: (e) => {
                if (!dragColId || dragColId === id) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                const after = isAfterMidpoint(e)
                if (overColId !== id) setOverColId(id)
                if (overAfter !== after) setOverAfter(after)
            },
            onDrop: (e) => {
                e.preventDefault()
                if (dragColId && dragColId !== id) onReorderColumns?.(dragColId, id, isAfterMidpoint(e))
                clearColDrag()
            },
            onDragEnd: clearColDrag,
        }
    }
    // Grab cursor + a drop-edge indicator (left/right border) on the hovered header; empty when off.
    const headerDragClass = (id: LoadColumnId): string =>
        reorderableColumns
            ? cn(
                  'cursor-grab active:cursor-grabbing',
                  dragColId === id && 'opacity-40',
                  overColId === id && (overAfter ? 'border-r-2 border-r-[var(--lh-accent)]' : 'border-l-2 border-l-[var(--lh-accent)]')
              )
            : ''

    const toggleExpand = useCallback(
        (loadUid: string) => {
            if (isControlledExpand) {
                onExpandToggle?.(loadUid)
            } else {
                setInternalExpandedIds((prev) => {
                    const next = new Set(prev)
                    if (next.has(loadUid)) next.delete(loadUid)
                    else next.add(loadUid)
                    return next
                })
                // Mark as viewed (uncontrolled viewed state only)
                if (viewedIdsProp === undefined) {
                    setInternalViewedIds((prev) => {
                        if (prev.has(loadUid)) return prev
                        const next = new Set(prev)
                        next.add(loadUid)
                        return next
                    })
                }
            }
        },
        [isControlledExpand, onExpandToggle, viewedIdsProp]
    )

    const handleRowClick = useCallback(
        (loadUid: string, event: React.MouseEvent) => {
            const target = event.target as HTMLElement
            if (target.closest('input[type="checkbox"]') || target.closest('button') || target.closest('a')) return
            toggleExpand(loadUid)
        },
        [toggleExpand]
    )

    const getSourceIcon = useCallback((sourceType: string) => {
        switch (sourceType?.toUpperCase()) {
            case 'TELEGRAM':
                return <TelegramIcon />
            case 'PARADE':
                return <ParadeIcon />
            case 'HAPPYROBOT':
                return <HappyRobotIcon />
            case 'TQL':
                return <TqlIcon />
            case 'ITF':
                return <ItfIcon />
            case 'PLS':
                return <PlsIcon />
            case 'BEON':
                return <BeonIcon />
            case 'NAVISPHERE':
                return <NavisphereIcon />
            case 'ARRIVE':
                return <ArriveIcon />
            case 'ECHO':
                return <EchoIcon />
            case 'TRUCKSTOP':
                return <img src="/truckstop-logo.webp" alt="Truckstop" style={{ height: '20px', objectFit: 'contain' }} />
            case 'TRUCKSTOP_ACCOUNT':
                return <img src="/truckstop_account.jpg" alt="TruckStop Account" style={{ height: '20px', objectFit: 'contain' }} />
            case 'SPOT':
                return <SpotIcon />
            case 'EMERGE':
                return <EmergeIcon />
            case 'MODE_GLOBAL':
                return <ModeGlobalIcon />
            case 'JB_HUNT':
                return <img src="/jb_hunt_logo.png" alt="JB Hunt" style={{ height: '20px', objectFit: 'contain' }} />
            case 'RXO':
                return <img src="/rxo_lb.png" alt="RXO" style={{ height: '20px', objectFit: 'contain' }} />
            case 'CONVOY':
                return <img src="/convoy.png" alt="Convoy" style={{ height: '20px', objectFit: 'contain' }} />
            case 'KNX':
                return <KnxIcon />
            case 'SCHNEIDER':
                return <SchneiderIcon />
            case 'SYLECTUS':
                return <img src="/sylectus.webp" alt="Sylectus" style={{ height: '20px', objectFit: 'contain', borderRadius: '4px' }} />
            case 'UBER_FREIGHT':
                return <img src="/uber_lb.png" alt="Uber Freight" style={{ height: '20px', objectFit: 'contain' }} />
            case 'DAT':
            case 'DAT_EXTENSION':
                return <DatOneIcon />
            default:
                return null
        }
    }, [])

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            updateScrollWindow(e.currentTarget)
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
            if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !isLoadingMore && onLoadMore) {
                onLoadMore()
            }
        },
        [hasMore, isLoadingMore, onLoadMore, updateScrollWindow]
    )

    const showHideSelected = Boolean(selectedLoads.length > 0 && onHideSelected)
    const showDupeNote = duplicateCount > 0 && hideDuplicates
    const hasToolbar = showHideSelected || showDupeNote
    // Whether the table has an extra "actions" column at the right edge
    const hasActionsCol = rowActions !== undefined
    const extraLeadingCols = (showClassColumn ? 1 : 0) + (showRowNumber ? 1 : 0) + (showStatusColumn ? 1 : 0) + (onPinRow ? 1 : 0)
    const extraTrailingCols = (hasActionsCol ? 1 : 0) + (showStarColumn ? 1 : 0) + (showCloseColumn ? 1 : 0)
    const colSpanTotal = 13 + extraLeadingCols + extraTrailingCols

    return (
        <div className="h-full min-h-0 min-w-0 flex flex-col bg-[var(--lh-bg)] load-hub-root">
            {/* Optional slim toolbar — only rendered when there's something to show (selected actions / dupe note). Search + global filters live in LoadFilters above. */}
            {hasToolbar && (
                <div className="shrink-0 flex items-center gap-2 px-4 py-1.5 bg-[var(--lh-surface)] border-b border-[var(--lh-border)] flex-wrap">
                    {showHideSelected && (
                        <button
                            type="button"
                            onClick={onHideSelected}
                            title={`Hide ${selectedLoads.length} selected load(s)`}
                            className="h-[28px] px-3 rounded-md border border-[var(--lh-border)] bg-[var(--lh-surface)] text-[var(--lh-fg)] text-[12px] font-medium flex items-center gap-1.5 flex-shrink-0 cursor-pointer hover:bg-[var(--lh-surface-secondary)] transition-colors"
                        >
                            <VisibilityOff sx={{ fontSize: 14 }} />
                            Hide selected ({selectedLoads.length})
                        </button>
                    )}
                    {showDupeNote && (
                        <span className="text-[11px] font-medium text-[var(--lh-fg-muted)]">
                            {duplicateCount} duplicate{duplicateCount === 1 ? '' : 's'} hidden
                        </span>
                    )}
                </div>
            )}

            <div
                ref={tableContainerRef}
                className="loads-table-scroll flex-1 min-w-0 overflow-auto flex flex-col relative bg-[var(--lh-bg)]"
                style={{ overflowAnchor: 'none' }}
                onScroll={handleScroll}
            >
                {mainLoads.length > 0 || processingLoads.length > 0 || loading ? (
                    <table className="w-full border-collapse flex-shrink-0">
                        <thead className="sticky top-0 z-[10] bg-[var(--lh-surface)] border-b border-[var(--lh-border)]">
                            {/* Reference column order: Age · SRC · Rate · RPM · Miles · [Map] · Origin · DH-O · Dest · DH-D · Equipment · Company · [Action] · Contact */}
                            <tr className="[&_th]:py-2 [&_th]:text-[12px] [&_th]:font-medium [&_th]:text-[var(--lh-fg-muted)] [&_th]:text-left [&_th]:select-none">
                                {showClassColumn && <th className="px-1 w-[28px]" aria-label="Class" />}
                                {showRowNumber && <th className="px-2 w-[32px] text-center">#</th>}
                                {onPinRow && <th className="px-1 w-[28px] text-center" aria-label="Pinned" />}
                                {(() => {
                                    // Movable header cells keyed by column id; emitted in `columnOrder` and made
                                    // draggable when reorderableColumns. Leading (class/#/pin) + trailing
                                    // (action/star/close) headers stay fixed, outside this block.
                                    const headerCells: Partial<Record<LoadColumnId, React.ReactNode>> = {
                                        age: (
                                            <th key="age" {...headerDragProps('age')} className={cn('px-2 min-w-[56px]', headerDragClass('age'))}>
                                                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                                    Age <FilterIndicator sort={ageSort} onSort={handleAgeSort} />
                                                </span>
                                            </th>
                                        ),
                                        src: (
                                            <th key="src" {...headerDragProps('src')} className={cn('px-2 min-w-[28px]', headerDragClass('src'))}>
                                                SRC
                                            </th>
                                        ),
                                        rate: (
                                            <th key="rate" {...headerDragProps('rate')} className={cn('px-2 min-w-[72px]', headerDragClass('rate'))}>
                                                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                                    Rate <FilterIndicator sort={rateSort} onSort={handleRateSort} />
                                                </span>
                                            </th>
                                        ),
                                        rpm: (
                                            <th key="rpm" {...headerDragProps('rpm')} className={cn('px-2 min-w-[60px]', headerDragClass('rpm'))}>
                                                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                                    RPM <FilterIndicator sort={rpmSort} onSort={handleRpmSort} />
                                                </span>
                                            </th>
                                        ),
                                        miles: (
                                            <th key="miles" {...headerDragProps('miles')} className={cn('px-2 min-w-[54px]', headerDragClass('miles'))}>
                                                Miles
                                            </th>
                                        ),
                                        map: <th key="map" {...headerDragProps('map')} className={cn('px-2 w-[22px]', headerDragClass('map'))} />,
                                        origin: (
                                            <th key="origin" {...headerDragProps('origin')} className={cn('px-2 min-w-[120px]', headerDragClass('origin'))}>
                                                Origin
                                            </th>
                                        ),
                                        dhO: (
                                            <th key="dhO" {...headerDragProps('dhO')} className={cn('px-2 min-w-[48px]', headerDragClass('dhO'))}>
                                                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                                    DH-O <FilterIndicator sort={dhOSort} onSort={handleDhOSort} />
                                                </span>
                                            </th>
                                        ),
                                        destination: (
                                            <th
                                                key="destination"
                                                {...headerDragProps('destination')}
                                                className={cn('px-2 min-w-[120px]', headerDragClass('destination'))}
                                            >
                                                Destination
                                            </th>
                                        ),
                                        dhD: (
                                            <th key="dhD" {...headerDragProps('dhD')} className={cn('px-2 min-w-[48px]', headerDragClass('dhD'))}>
                                                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                                    DH-D <FilterIndicator sort={dhDSort} onSort={handleDhDSort} />
                                                </span>
                                            </th>
                                        ),
                                        equipment: (
                                            <th
                                                key="equipment"
                                                {...headerDragProps('equipment')}
                                                className={cn('px-2 min-w-[180px]', headerDragClass('equipment'))}
                                            >
                                                Equipment
                                            </th>
                                        ),
                                        company: (
                                            <th key="company" {...headerDragProps('company')} className={cn('px-2 min-w-[140px]', headerDragClass('company'))}>
                                                Company
                                            </th>
                                        ),
                                        contact: (
                                            <th key="contact" {...headerDragProps('contact')} className={cn('px-2 min-w-[160px]', headerDragClass('contact'))}>
                                                Contact
                                            </th>
                                        ),
                                    }
                                    if (showStatusColumn) {
                                        headerCells.status = (
                                            <th
                                                key="status"
                                                {...headerDragProps('status')}
                                                className={cn('px-2 w-[150px] min-w-[150px] max-w-[150px]', headerDragClass('status'))}
                                            >
                                                {statusHeaderSlot ?? 'Status'}
                                            </th>
                                        )
                                    }
                                    return (columnOrder ?? DEFAULT_LOAD_COLUMN_ORDER).map((id) => headerCells[id])
                                })()}
                                {hasActionsCol && <th className="px-2 min-w-[80px]"></th>}
                                {showStarColumn && <th className="px-2 w-[32px]"></th>}
                                {showCloseColumn && <th className="px-2 w-[32px] sticky right-0 bg-[var(--lh-surface)] z-[5]"></th>}
                            </tr>
                            {/* Scanning stripe — sticky with header, ds/ProgressBar indeterminate. */}
                            {isRefreshing && (
                                <tr>
                                    <th colSpan={colSpanTotal} className="p-0 border-none h-[2px]">
                                        <ProgressBar indeterminate tone="accent" height={2} aria-label="Searching loads" className="!rounded-none" />
                                    </th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {loading && mainLoads.length === 0 && processingLoads.length === 0 && (
                                <SkeletonRows
                                    count={skeletonRowCount}
                                    showClassColumn={showClassColumn}
                                    showRowNumber={showRowNumber}
                                    hasPinColumn={!!onPinRow}
                                    showStatusColumn={showStatusColumn}
                                    hasActionsCol={hasActionsCol}
                                    showStarColumn={showStarColumn}
                                    showCloseColumn={showCloseColumn}
                                    columnOrder={columnOrder ?? DEFAULT_LOAD_COLUMN_ORDER}
                                />
                            )}

                            {/* Sync-delta processing banner — collapsible header row pinned above
                                the main list. Click to expand and reveal the in-flight rows
                                rendered with the same LoadRow component. Auto-collapses when
                                processingCount drops to 0 (see effect above). */}
                            {processingCount > 0 && (
                                <tr
                                    className="border-b border-gray-100 dark:border-white/[0.04] bg-indigo-50/40 dark:bg-indigo-500/[0.06] hover:bg-indigo-50/60 dark:hover:bg-indigo-500/10 cursor-pointer transition-colors"
                                    onClick={() => setProcessingExpanded((v) => !v)}
                                >
                                    <td colSpan={colSpanTotal} className="py-2 px-4">
                                        <div className="flex items-center gap-2 text-[12px] text-indigo-700 dark:text-indigo-300">
                                            <svg className="h-3.5 w-3.5 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                            </svg>
                                            <span className="font-medium">
                                                {processingCount} new load{processingCount === 1 ? '' : 's'} being processed
                                            </span>
                                            <span className="text-indigo-500/70 dark:text-indigo-400/70">(filtering &amp; ranking)</span>
                                            <span className="ml-auto flex items-center gap-1 text-[11px] text-indigo-600/80 dark:text-indigo-300/80">
                                                {processingExpanded ? 'Hide' : 'Show'}
                                                <svg
                                                    className={cn('h-3 w-3 transition-transform', processingExpanded && 'rotate-180')}
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {processingExpanded &&
                                processingLoads.map((load, index) => {
                                    const loadUid = getLoadUid(load)
                                    return (
                                        <LoadRow
                                            key={loadUid}
                                            load={load}
                                            loadUid={loadUid}
                                            index={index}
                                            isExpanded={expandedLoadIds.has(loadUid)}
                                            isSelected={selectedSet.has(load.id)}
                                            isViewed={viewedLoads.has(loadUid)}
                                            isEmailOpen={!!emailOpenLoadUid && loadUid === emailOpenLoadUid}
                                            originLocation={originLocation}
                                            originLocationData={originLocationData}
                                            onSelectLoad={onSelectLoad}
                                            onToggleExpand={toggleExpand}
                                            onRowClick={handleRowClick}
                                            onLoadUpdate={onLoadUpdate}
                                            onEmailClick={onEmailClick}
                                            onOpenThread={onOpenThread}
                                            sendingEmail={sendingLoads?.has(loadUid)}
                                            errorEmail={errorLoads?.has(loadUid)}
                                            emailThreadId={emailThreads?.get(loadUid)}
                                            onCallClick={onCallClick}
                                            hasPhone={hasPhoneLoads?.has(loadUid)}
                                            isCalling={callingLoads?.has(loadUid)}
                                            callError={errorCallLoads?.has(loadUid)}
                                            callInProgress={activeCallLoadUids?.has(loadUid) ?? false}
                                            hasBeenCalled={calledLoadUids?.has(loadUid) ?? false}
                                            renderCallControls={renderCallControls}
                                            loadMongoId={loadMongoIds?.get(loadUid)}
                                            unreadCount={unreadCounts?.get(loadUid) ?? 0}
                                            onResetUnread={onResetUnread}
                                            getSourceIcon={getSourceIcon}
                                            isNew={newLoadUids?.has(loadUid) ?? false}
                                            rowMeta={getRowMeta?.(load)}
                                            draggableRows={draggableRows}
                                            rowActions={rowActions}
                                            renderContactExtras={renderContactExtras}
                                            showRowNumber={showRowNumber}
                                            showStatusColumn={showStatusColumn}
                                            showStarColumn={showStarColumn}
                                            showCloseColumn={showCloseColumn}
                                            onPinRow={onPinRow}
                                            renderRateCell={renderRateCell}
                                            renderRpmCell={renderRpmCell}
                                            renderDestinationCell={renderDestinationCell}
                                            renderExpandedContent={renderExpandedContent}
                                            onCloseRow={onCloseRow}
                                            columnOrder={columnOrder}
                                            analyticsSurface={analyticsSurface}
                                        />
                                    )
                                })}

                            {(() => {
                                const renderLoadRow = (load: CanonicalLoad, index: number) => {
                                    const loadUid = getLoadUid(load)
                                    return (
                                        <LoadRow
                                            key={loadUid}
                                            load={load}
                                            loadUid={loadUid}
                                            index={index}
                                            isExpanded={expandedLoadIds.has(loadUid)}
                                            isSelected={selectedSet.has(load.id)}
                                            isViewed={viewedLoads.has(loadUid)}
                                            isEmailOpen={!!emailOpenLoadUid && loadUid === emailOpenLoadUid}
                                            originLocation={originLocation}
                                            originLocationData={originLocationData}
                                            onSelectLoad={onSelectLoad}
                                            onToggleExpand={toggleExpand}
                                            onRowClick={handleRowClick}
                                            onLoadUpdate={onLoadUpdate}
                                            onEmailClick={onEmailClick}
                                            onOpenThread={onOpenThread}
                                            sendingEmail={sendingLoads?.has(loadUid)}
                                            errorEmail={errorLoads?.has(loadUid)}
                                            emailThreadId={emailThreads?.get(loadUid)}
                                            onCallClick={onCallClick}
                                            hasPhone={hasPhoneLoads?.has(loadUid)}
                                            isCalling={callingLoads?.has(loadUid)}
                                            callError={errorCallLoads?.has(loadUid)}
                                            callInProgress={activeCallLoadUids?.has(loadUid) ?? false}
                                            hasBeenCalled={calledLoadUids?.has(loadUid) ?? false}
                                            renderCallControls={renderCallControls}
                                            loadMongoId={loadMongoIds?.get(loadUid)}
                                            unreadCount={unreadCounts?.get(loadUid) ?? 0}
                                            onResetUnread={onResetUnread}
                                            getSourceIcon={getSourceIcon}
                                            isNew={newLoadUids?.has(loadUid) ?? false}
                                            rowMeta={getRowMeta?.(load)}
                                            draggableRows={draggableRows}
                                            rowActions={rowActions}
                                            renderContactExtras={renderContactExtras}
                                            showRowNumber={showRowNumber}
                                            showClassColumn={showClassColumn}
                                            showStatusColumn={showStatusColumn}
                                            showStarColumn={showStarColumn}
                                            showCloseColumn={showCloseColumn}
                                            onPinRow={onPinRow}
                                            renderRateCell={renderRateCell}
                                            renderRpmCell={renderRpmCell}
                                            renderDestinationCell={renderDestinationCell}
                                            renderExpandedContent={renderExpandedContent}
                                            onCloseRow={onCloseRow}
                                            columnOrder={columnOrder}
                                            analyticsSurface={analyticsSurface}
                                        />
                                    )
                                }

                                if (!virtualize) return mainLoads.map(renderLoadRow)

                                // Windowed render — off-screen runs collapse into fixed-height
                                // spacer rows; expanded rows always render for real so their
                                // content-driven height stays in the layout.
                                const out: React.ReactNode[] = []
                                let spacerRows = 0
                                let spacerKey = 0
                                const flushSpacer = () => {
                                    if (spacerRows > 0) {
                                        out.push(<tr key={`row-spacer-${spacerKey++}`} aria-hidden style={{ height: spacerRows * rowH }} />)
                                        spacerRows = 0
                                    }
                                }
                                mainLoads.forEach((load, index) => {
                                    const inWindow = index >= winStart && index < winEnd
                                    if (inWindow || expandedLoadIds.has(getLoadUid(load))) {
                                        flushSpacer()
                                        out.push(renderLoadRow(load, index))
                                    } else {
                                        spacerRows++
                                    }
                                })
                                flushSpacer()
                                return out
                            })()}
                        </tbody>
                    </table>
                ) : suppressEmptyState ? null : (
                    <EmptyState
                        icon={<EmptyStateIcon />}
                        title={<span className="text-base font-semibold text-slate-900 dark:text-slate-100 tracking-tight">No loads found</span>}
                        description="Try adjusting your filters, changing the route, or expanding the date range to find more loads"
                        className="py-16"
                    />
                )}

                {isLoadingMore && (
                    <div className="flex items-center justify-center py-4 flex-shrink-0">
                        <span className="animate-spin h-4 w-4 border-2 border-[var(--lh-accent)] border-t-transparent rounded-full" />
                        <span className="ml-2 text-[11px] font-medium text-[var(--lh-fg-muted)]">Loading</span>
                    </div>
                )}

                {/* Bottom breathing room so the floating "Ask Numeo AI" pill doesn't cover the last rows
                    when scrolled to the end. Opt-in via bottomSpacerHeight (agentic-hub only). */}
                {bottomSpacerHeight != null && mainLoads.length > 0 && <div aria-hidden className="shrink-0" style={{ height: bottomSpacerHeight }} />}
            </div>
        </div>
    )
}
